<template>
    <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <!-- Title -->
        <div class="border-b border-gray-200 bg-gray-100 px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
            <h4 class="text-center text-base font-semibold text-gray-900 underline dark:text-white">
                {{ title }}
            </h4>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto">
            <table class="w-full text-left text-sm">
                <!-- Header -->
                <thead class="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                <tr>
                    <th
                            v-for="(column, index) in columns"
                            :key="index"
                            class="px-4 py-3 font-medium"
                            :class="getColumnAlignment(index)"
                    >
                        {{ column }}
                    </th>
                </tr>
                </thead>

                <!-- Body -->
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                <!-- Data Rows -->
                <template v-if="data.length > 0">
                    <tr
                            v-for="(row, rowIndex) in data"
                            :key="rowIndex"
                            class="bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700"
                    >
                        <td
                                v-for="(cell, cellIndex) in row"
                                :key="cellIndex"
                                class="px-4 py-3 text-gray-900 dark:text-white"
                                :class="getCellAlignment(cellIndex)"
                                :data-label="columns[cellIndex] + ' : '"
                        >
                            {{ cell }}
                        </td>
                    </tr>

                    <!-- Total Row (simple) -->
                    <tr
                            v-if="total !== null && !totalsRow"
                            class="bg-gray-100 font-semibold dark:bg-gray-900"
                    >
                        <td :colspan="totalColspan" class="px-4 py-3 text-gray-900 dark:text-white">
                            {{ totalLabel }}
                        </td>
                        <td class="px-4 py-3 text-right text-gray-900 dark:text-white">
                            {{ formatTotal(total) }}
                        </td>
                    </tr>

                    <!-- Totals Row (multiple) -->
                    <tr
                            v-if="totalsRow"
                            class="bg-gray-100 font-semibold dark:bg-gray-900"
                    >
                        <td
                                v-for="(cell, index) in totalsRow"
                                :key="index"
                                class="px-4 py-3 text-gray-900 dark:text-white"
                                :class="index < totalColspan ? '' : 'text-right'"
                        >
                            {{ cell }}
                        </td>
                    </tr>
                </template>

                <!-- Empty State -->
                <template v-else>
                    <tr>
                        <td :colspan="columns.length" class="px-4 py-8">
                            <div class="flex items-center justify-center gap-2 rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{{ emptyMessage }}</span>
                            </div>
                        </td>
                    </tr>
                </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
    import { currencyFormatDE } from "@/allfunction";

    export default {
        name: "DataTable",

        props: {
            // Table title
            title: {
                type: String,
                required: true
            },

            // Column headers array
            columns: {
                type: Array,
                required: true
            },

            // Data rows (array of arrays)
            data: {
                type: Array,
                default: () => []
            },

            // Simple total value (last column)
            total: {
                type: [Number, String],
                default: null
            },

            // Label for total row
            totalLabel: {
                type: String,
                default: "Total"
            },

            // Colspan for total label
            totalColspan: {
                type: Number,
                default: 1
            },

            // Multiple totals row (array)
            totalsRow: {
                type: Array,
                default: null
            },

            // Show total row
            showTotal: {
                type: Boolean,
                default: true
            },

            // Message when no data
            emptyMessage: {
                type: String,
                default: "Aucune donnée trouvée"
            },

            // Columns to align right (usually money columns)
            rightAlignColumns: {
                type: Array,
                default: () => []
            }
        },

        methods: {
            formatTotal(value) {
                if (typeof value === 'number') {
                    return currencyFormatDE(value);
                }
                return value;
            },

            getColumnAlignment(index) {
                // Last column or specified columns are right-aligned
                if (index === this.columns.length - 1 || this.rightAlignColumns.includes(index)) {
                    return 'text-right';
                }
                return '';
            },

            getCellAlignment(index) {
                // Last column or specified columns are right-aligned
                if (index === this.columns.length - 1 || this.rightAlignColumns.includes(index)) {
                    return 'text-right';
                }
                return '';
            }
        }
    };
</script>

<style scoped>
    /* Responsive table styles */
    @media screen and (max-width: 768px) {
        table {
            border: 0;
        }

        table thead {
            display: none;
        }

        table tr {
            display: block;
            margin-bottom: 0.625rem;
            border: 1px solid #e5e7eb;
            border-radius: 0.5rem;
        }

        table td {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem 1rem;
            border-bottom: 1px solid #e5e7eb;
            text-align: right !important;
        }

        table td::before {
            content: attr(data-label);
            font-weight: 600;
            text-align: left;
            margin-right: 1rem;
        }

        table td:last-child {
            border-bottom: 0;
        }
    }
</style>